import React, { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import CharityStackMark from '../assets/CharityStack-Mark.png';

/** Stable default so `messages` default does not break useMemo. */
const EMPTY_MESSAGES = [];

/**
 * Shared polished loading visuals: stationary sage mark, indeterminate aurora bar,
 * pulsing skeleton lines, optional rotating messages — matches onboarding + dashboard loading.
 *
 * @param {object} props
 * @param {string} props.title - Primary heading (e.g. "Setting up your dashboard")
 * @param {string[]} props.messages - Subtext lines; cycled sequentially when length > 1
 * @param {number} props.messageIntervalMs
 * @param {boolean} props.showLogo
 * @param {boolean} props.compact - Tighter layout for inline/dropdown (no logo, narrower card feel)
 */
export default function OnboardingLoadingPattern({
  title,
  messages = EMPTY_MESSAGES,
  messageIntervalMs = 2500,
  showLogo = true,
  compact = false,
  className = '',
}) {
  const messageList = useMemo(() => {
    if (messages?.length) return messages;
    return [''];
  }, [messages]);
  const [setupMsg, setSetupMsg] = useState(() => messageList[0]);

  useEffect(() => {
    setSetupMsg(messageList[0]);
  }, [messageList]);

  useEffect(() => {
    if (messageList.length <= 1) return undefined;
    let msgIdx = 0;
    const msgTimer = setInterval(() => {
      msgIdx = (msgIdx + 1) % messageList.length;
      setSetupMsg(messageList[msgIdx]);
    }, messageIntervalMs);
    return () => clearInterval(msgTimer);
  }, [messageList, messageIntervalMs]);

  const cardClass = compact
    ? 'mb-3 w-full max-w-full rounded-lg bg-paper-light p-2.5 shadow-sm'
    : 'mb-4 w-[220px] rounded-lg bg-paper-light p-3.5 shadow-md sm:mb-6';

  return (
    <div className={`flex flex-col items-center justify-center ${className}`}>
      <style>{`
        @keyframes auroraShift {
          0%   { background-position: 0% 50%; }
          50%  { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
        @keyframes indeterminateWidth {
          0%   { width: 30%; }
          50%  { width: 85%; }
          100% { width: 30%; }
        }
        @keyframes skeletonPulse {
          0%, 100% { opacity: 1; }
          50%      { opacity: 0.4; }
        }
      `}</style>

      {showLogo && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
          className={compact ? 'mb-3' : 'mb-6'}
        >
          <div
            className={`mx-auto flex items-center justify-center ${compact ? 'mb-2 h-11 w-11' : 'mb-4 h-14 w-14'}`}
          >
            <img
              src={CharityStackMark}
              alt="CharityStack"
              className="h-full w-full object-contain object-center"
            />
          </div>
        </motion.div>
      )}

      <div className={cardClass}>
        <div
          className={`mb-2.5 rounded-full ${compact ? 'h-1' : 'h-[5px]'}`}
          style={{
            background: 'linear-gradient(90deg, #5B785B, #6B8E6B, #7FA87F, #6B8E6B, #5B785B)',
            backgroundSize: '300% 100%',
            animation: 'auroraShift 4s ease-in-out infinite, indeterminateWidth 2.5s ease-in-out infinite',
          }}
        />
        {[100, 85, 65].map((width, index) => (
          <div
            key={index}
            className={`mb-1.5 rounded-full bg-paper-inset last:mb-0 ${compact ? 'h-1.5' : 'h-2'}`}
            style={{
              width: `${width}%`,
              animation: 'skeletonPulse 1.8s ease-in-out infinite',
              animationDelay: `${index * 0.15}s`,
            }}
          />
        ))}
      </div>

      {(title || setupMsg) && (
        <motion.div
          className={`${compact ? 'mb-0 max-w-full text-left' : 'mb-4 text-center sm:mb-6'}`}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          {title ? (
            <p
              className={`mb-1 font-semibold text-ink ${compact ? 'text-sm' : 'text-base sm:text-lg'}`}
            >
              {title}
            </p>
          ) : null}
          {setupMsg ? (
            <div className={`w-full overflow-hidden ${compact ? 'min-h-4' : 'h-5'}`}>
              <AnimatePresence mode="wait">
                <motion.p
                  key={setupMsg}
                  initial={{ opacity: 0, y: compact ? 8 : 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: compact ? -8 : -20 }}
                  transition={{ duration: 0.5 }}
                  className={`text-ink-secondary ${compact ? 'text-xs' : 'whitespace-nowrap text-xs sm:text-sm'}`}
                >
                  {setupMsg}
                </motion.p>
              </AnimatePresence>
            </div>
          ) : null}
        </motion.div>
      )}
    </div>
  );
}
